import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, articles, type Article } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  jsonLd,
}: {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  jsonLd?: Record<string, unknown>;
}) {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', keywords);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicalUrl);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', description);

    if (jsonLd) {
      const existingScript = document.getElementById('page-jsonld');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, jsonLd]);

  return null;
}

function CategoryLabel({ category }: { category: string }) {
  const labelMap: Record<string, string> = {
    'brand-news': '品牌动态',
    'industry-insight': '行业洞察',
    'product-service': '产品服务',
    'tech-frontier': '技术前沿',
    'customer-case': '客户案例',
  };
  return (
    <Link
      to={`/category/${category}`}
      className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
    >
      {labelMap[category] || category}
    </Link>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">文章不存在</h1>
          <p className="text-foreground-500 text-sm mb-6">该文章可能已被移除或链接错误</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const categoryLabel =
    article.category === 'brand-news' ? '品牌动态' :
    article.category === 'industry-insight' ? '行业洞察' :
    article.category === 'product-service' ? '产品服务' :
    article.category === 'tech-frontier' ? '技术前沿' : '客户案例';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    url: `https://hecaoqing.com.cn/article/${article.slug}`,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '问鼎.cc',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hecaoqing.com.cn/' },
        { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://hecaoqing.com.cn/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://hecaoqing.com.cn/article/${article.slug}` },
      ],
    },
  };

  return (
    <>
      <SeoHead
        title={`${article.title} - 问鼎.cc`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://hecaoqing.com.cn/article/${article.slug}`}
        jsonLd={articleJsonLd}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <article>
          {/* Article Header */}
          <header className="max-w-3xl mx-auto px-6 pt-12 md:pt-16">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryLabel, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
            <div className="mt-4">
              <CategoryLabel category={article.category} />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 mt-5 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-6 text-sm text-foreground-400 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-6 mt-8">
            <div className="aspect-[21/9] rounded-2xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 问鼎.cc`}
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-5
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:text-[15px]
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-li:text-foreground-600 prose-li:text-[15px] prose-li:leading-relaxed
                prose-ul:list-disc prose-ol:list-decimal
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600
                [&_h2]:border-l-4 [&_h2]:border-primary-500 [&_h2]:pl-4
                [&_h3]:pl-0
                [&_ul]:space-y-1.5 [&_ol]:space-y-1.5
                [&_li::marker]:text-primary-400"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {/* Tags */}
          <div className="max-w-3xl mx-auto px-6 pb-8">
            <div className="flex items-center gap-2 flex-wrap pt-6 border-t border-background-200/70">
              <span className="text-xs text-foreground-400 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-500 bg-background-100 px-3 py-1 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group block rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={`${rel.title} - 问鼎.cc`}
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 text-xs text-foreground-400">
                        <span>{rel.date}</span>
                        <span>{rel.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 font-heading">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}